import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Grid, Button, Box } from '@mui/material'
import { translate } from '../i18n'
import { updateBasicProfile } from '../api'
import { User, BasicProfileRequest } from '../model'
import { phoneFormat, PhoneValidator } from '../validators'
import { FeedbackContext } from '../feedback'
import { AnyInput, AnyMaskedInput, useValidatedForm } from '../anyform'
import { AppContext } from '../context'
import { Surface, ProfileIcon } from '../components'

export default function UserBasicProfileForm() {
    const feedbackContext = useContext(FeedbackContext)
    const context = useContext(AppContext)
    const sessionUser = context.session?.user

    const { register, bind, isValid } = useValidatedForm<BasicProfileRequest>()

    const user = useMemo(() => sessionUser!, [sessionUser])
    const validators = useMemo(() => [new PhoneValidator()], [])
    const [format, setFormat] = useState('')

    const setUser = useCallback(
        (user: User) => {
            if (!context.session) return

            const curr = context.session.user
            context.session.user = {
                ...curr,
                first_name: user.first_name,
                last_name: user.last_name,
                phone: user.phone,
            }
        },
        [context.session]
    )

    const hasChangedPhone = useCallback((name: string, value?: unknown) => {
        const newFormat = phoneFormat(value ? (value as string) : '')
        setFormat((curr) => {
            if (curr !== newFormat) {
                return newFormat
            }
            return curr
        })
    }, [])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()
        updateBasicProfile(bind())
            .then((user) => {
                feedbackContext.showSuccess(
                    translate('users.profile.updated_basic') as string
                )
                setUser(user)
            })
            .catch((error) => {
                feedbackContext.showError(error.message)
            })
            .finally(() => {
                feedbackContext.closeBackdrop()
            })
    }

    return (
        <Surface
            title={translate('users.profile.basic') as string}
            icon={<ProfileIcon />}
        >
            <Grid
                container
                alignItems="center"
                justifyContent="center"
                alignContent="center"
            >
                <Grid item xs={12}>
                    <form autoComplete="off" noValidate onSubmit={onSubmit}>
                        <Grid container>
                            <Grid item xs={12}>
                                <AnyInput
                                    type="text"
                                    name="first_name"
                                    label={
                                        translate('users.first_name') as string
                                    }
                                    initialValue={user.first_name}
                                    register={register}
                                    required
                                />
                                <AnyInput
                                    type="text"
                                    name="last_name"
                                    label={
                                        translate('users.last_name') as string
                                    }
                                    initialValue={user.last_name}
                                    register={register}
                                    required
                                />
                                <AnyMaskedInput
                                    name="phone"
                                    label={translate('users.phone') as string}
                                    initialValue={user.phone}
                                    register={register}
                                    notifier={hasChangedPhone}
                                    validators={validators}
                                    mask=" "
                                    format={format}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box my={2}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        size="large"
                                    >
                                        {translate('buttons.update')}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </Grid>
            </Grid>
        </Surface>
    )
}
